import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation';
import styles from './styles';
import { useTranslation } from 'react-i18next';
import { textToSnakeCase, formatYYYYMMDD } from '../../../utils/utils';
import DateFnsUtils from '@date-io/date-fns';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from 'moment';
import { Clear as ClearIcon } from '@material-ui/icons';
import {
  fr,
  enUS,
  ar,
  cs,
  da,
  de,
  es,
  zhCN,
  nl,
  id,
  it,
  ja,
  ko,
  pt,
  ru,
  sv,
  th,
  tr,
  zhTW,
  fi,
} from 'date-fns/locale';
import { parseISO } from 'date-fns';
import {
  formatDate,
  isPurchaseDateBeforeExpiryDate,
} from './helpers/equipmentWarranty';
export const localesMap = {
  frc: fr,
  fre: fr,
  eng: enUS,
  ara: ar,
  cze: cs,
  dan: da,
  ger: de,
  spa: es,
  spl: es,
  chs: zhCN,
  dut: nl,
  ind: id,
  fil: fi,
  ita: it,
  jpn: ja,
  kor: ko,
  ptb: pt,
  rus: ru,
  swe: sv,
  tai: th,
  tur: tr,
  cht: zhTW,
};

const SpecificDataRow = ({
  selectedLanguage,
  brand,
  index,
  piece,
  handleChangeSelect,
  handleChangeTextField,
  handleChangeDateField,
  removeRow,
  calibrationProfiles,
  zones,
  removeOption = true,
}) => {
  const classes = styles();
  const { t } = useTranslation();

  const handleClrDateOfPurchase = e => {
    handleChangeDateField(`equipment_purchase_date-${index}`, null);
    e.stopPropagation();
  };

  const handleClrDateOfExpiry = e => {
    handleChangeDateField(`equipment_date_of_expiry-${index}`, null);
    e.stopPropagation();
  };

  const handleChangeDateOfExpiry = date => {
    handleChangeDateField(
      `equipment_date_of_expiry-${index}`,
      parseISO(moment(date).format(formatYYYYMMDD)),
    );
  };

  const handleChangeDateOfPurchase = date => {
    handleChangeDateField(
      `equipment_purchase_date-${index}`,
      parseISO(moment(date).format(formatYYYYMMDD)),
    );
  };

  return (
    <div
      className={classes.flexSpaceBetween}
      style={{
        borderBottom: '1px solid rgba(0,0,0,0.3)',
        margin: '10px 0',
      }}
    >
      <div
        className={classes.flexSpaceBetween}
        style={{
          width: '100%',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        }}
      >
        <TextField
          variant="outlined"
          id={`serial_number-${index}`}
          data-testid={`serial_number-${index}`}
          label={t('restaurant.info.equipment.serialNumber')}
          name={`serial_number-${index}`}
          size="small"
          style={{ width: '300px', marginLeft: '9px' }}
          value={piece.serial_number}
          onChange={handleChangeTextField}
          error={piece.serial_number.length > 100}
          helperText={
            piece.serial_number.length > 100
              ? t('restaurant.info.equipment.lengthError')
              : ''
          }
        />
        <TextField
          variant="outlined"
          id={`alternate_name-${index}`}
          label={t('restaurant.info.equipment.alternateName')}
          name={`alternate_name-${index}`}
          required
          size="small"
          style={{ width: '300px', marginLeft: '9px' }}
          value={piece.alternate_name}
          onChange={handleChangeTextField}
          error={piece.alternate_name.length > 100}
          helperText={
            piece.alternate_name.length > 100
              ? t('restaurant.info.equipment.lengthError')
              : ''
          }
        />
        <MuiPickersUtilsProvider
          utils={DateFnsUtils}
          locale={localesMap[selectedLanguage]}
        >
          <DatePicker
            style={{ width: '300px', margin: '8px 8px 8px 9px' }}
            size="small"
            autoOk
            variant="inline"
            inputVariant="outlined"
            id={`equipment_purchase_date-${index}`}
            label={t('createModel.DateOfPurchase')}
            format="yyyy-MM-dd"
            clearable
            disableFuture
            name={`equipment_purchase_date-${index}`}
            value={formatDate(piece.equipment_purchase_date)}
            onChange={(date: Date | null) => handleChangeDateOfPurchase(date)}
            InputProps={{
              endAdornment: piece.equipment_purchase_date ? (
                <InputAdornment position="end">
                  <IconButton onClick={e => handleClrDateOfPurchase(e)}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            error={
              !isPurchaseDateBeforeExpiryDate(
                piece.equipment_purchase_date,
                piece.equipment_date_of_expiry,
              )
            }
          />
        </MuiPickersUtilsProvider>
        <Box component="fieldset" style={{ marginLeft: '9px' }}>
          <FormLabel component="legend">
            {t('createModel.ExpiryTerm')}
          </FormLabel>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <TextField
              variant="outlined"
              id={`equipment_expiry_term_year-${index}`}
              label={t('createModel.Year')}
              name={`equipment_expiry_term_year-${index}`}
              size="small"
              style={{ width: '300px' }}
              value={piece.equipment_expiry_term_year}
              onChange={handleChangeTextField}
              error={piece.equipment_expiry_term_year?.length > 100}
              helperText={
                piece.equipment_expiry_term_year?.length > 100
                  ? t('restaurant.info.equipment.lengthError')
                  : ''
              }
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              InputLabelProps={{
                shrink: piece.equipment_expiry_term_year !== null,
              }}
            />
            <TextField
              variant="outlined"
              id={`equipment_expiry_term_month-${index}`}
              label={t('createModel.Month')}
              name={`equipment_expiry_term_month-${index}`}
              size="small"
              style={{ width: '300px' }}
              value={piece.equipment_expiry_term_month}
              onChange={handleChangeTextField}
              error={piece.equipment_expiry_term_month?.length > 100}
              helperText={
                piece.equipment_expiry_term_month?.length > 100
                  ? t('restaurant.info.equipment.lengthError')
                  : ''
              }
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              InputLabelProps={{
                shrink: piece.equipment_expiry_term_month !== null,
              }}
            />
            <TextField
              variant="outlined"
              id={`equipment_expiry_term_day-${index}`}
              label={t('createModel.Day')}
              name={`equipment_expiry_term_day-${index}`}
              size="small"
              style={{ width: '300px' }}
              value={piece.equipment_expiry_term_day}
              onChange={handleChangeTextField}
              error={piece.equipment_expiry_term_day?.length > 100}
              helperText={
                piece.equipment_expiry_term_day?.length > 100
                  ? t('restaurant.info.equipment.lengthError')
                  : ''
              }
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              InputLabelProps={{
                shrink: piece.equipment_expiry_term_day !== null,
              }}
            />
          </Box>
        </Box>
        <FormControl
          required
          className={classes.formControl}
          variant="outlined"
          size="small"
          style={{ width: '300px' }}
        >
          <InputLabel id="demo-simple-select-outlined-label">
            {t('restaurant.info.equipment.zone')}
          </InputLabel>
          <Select
            labelId="demo-simple-select-outlined-label"
            id="demo-simple-select-outlined"
            value={piece.restaurant_zone_id}
            onChange={handleChangeSelect}
            name={`restaurant_zone_id-${index}`}
          >
            <MenuItem value="0"></MenuItem>
            {zones.map(zone => (
              <MenuItem value={zone.id} key={zone.id}>
                {t(`equipment.zone.${textToSnakeCase(zone.name)}`, zone.name)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <MuiPickersUtilsProvider
          utils={DateFnsUtils}
          locale={localesMap[selectedLanguage]}
        >
          <DatePicker
            style={{ width: '300px', margin: '8px 8px 8px 9px' }}
            size="small"
            autoOk
            variant="inline"
            inputVariant="outlined"
            id={`equipment_date_of_expiry-${index}`}
            label={t('restaurant.info.equipment.DateOfExpiry')}
            format="yyyy-MM-dd"
            clearable
            name={`equipment_date_of_expiry-${index}`}
            value={formatDate(piece.equipment_date_of_expiry)}
            onChange={(date: Date | null) => handleChangeDateOfExpiry(date)}
            InputProps={{
              endAdornment: piece.equipment_date_of_expiry ? (
                <InputAdornment position="end">
                  <IconButton onClick={e => handleClrDateOfExpiry(e)}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            error={
              !isPurchaseDateBeforeExpiryDate(
                piece.equipment_purchase_date,
                piece.equipment_date_of_expiry,
              )
            }
          />
        </MuiPickersUtilsProvider>
        {brand === 'TH' && (
          <FormControl
            className={classes.formControl}
            variant="outlined"
            size="small"
            style={{ width: '300px' }}
          >
            <InputLabel id="demo-simple-select-outlined-label">
              {t('equipment.calibrationProfile')}
            </InputLabel>
            <Select
              labelId="demo-simple-select-outlined-label"
              id="demo-simple-select-outlined"
              value={
                piece.calibration_profile === null
                  ? ''
                  : piece.calibration_profile
              }
              onChange={handleChangeSelect}
              name={`calibration_profile-${index}`}
            >
              <MenuItem value=""></MenuItem>
              {calibrationProfiles.map(cp => (
                <MenuItem value={cp.id} key={cp.id}>
                  {cp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>
      {removeOption && (
        <IconButton
          onClick={removeRow(index)}
          aria-label={t('app.common.delete')}
        >
          <CancelPresentationIcon />
        </IconButton>
      )}
    </div>
  );
};

export default SpecificDataRow;
